(() => {
  const tagName = 'colorful-bunch-scroll-sticker';
  if (!window.customElements || customElements.get(tagName) || !window.matchMedia) return;

  class ScrollSticker extends HTMLElement {
    constructor() {
      super();
      this.frame = null;
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.update = this.update.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
      this.onSectionUnload = this.onSectionUnload.bind(this);
    }

    connectedCallback() {
      if (this.active || !this.hasAttribute('data-scroll-sticker')) return;
      this.active = true;

      // Measure the unrotated outer sticker, never the transformed content.
      this.anchor = this.parentElement;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', this.onMotionChange);
      window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleUpdate);
      window.addEventListener('pageshow', this.scheduleUpdate);
      window.addEventListener('load', this.scheduleUpdate);
      this.addEventListener('load', this.scheduleUpdate, true);

      // Custom-element connection/disconnection handles replacement and removal.
      // Other sections can move this sticker without changing its own size.
      this.editorEvents = ['shopify:section:load', 'shopify:section:reorder'];
      this.editorEvents.forEach((event) => document.addEventListener(event, this.scheduleUpdate));
      document.addEventListener('shopify:section:unload', this.onSectionUnload);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
        this.resizeObserver.observe(this.anchor);
        this.resizeObserver.observe(document.body);
      }

      this.scheduleUpdate();
    }

    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;
      this.motion.removeEventListener('change', this.onMotionChange);
      window.removeEventListener('scroll', this.scheduleUpdate);
      window.removeEventListener('resize', this.scheduleUpdate);
      window.removeEventListener('pageshow', this.scheduleUpdate);
      window.removeEventListener('load', this.scheduleUpdate);
      this.removeEventListener('load', this.scheduleUpdate, true);
      this.editorEvents.forEach((event) => document.removeEventListener(event, this.scheduleUpdate));
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      this.resizeObserver?.disconnect();
      this.reset();
    }

    reset() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.style.removeProperty('--scroll-sticker-angle');
    }

    onMotionChange() {
      this.reset();
      this.scheduleUpdate();
    }

    onSectionUnload(event) {
      if (event.target.contains(this)) this.disconnectedCallback();
      else this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (!this.active || !this.isConnected || this.motion.matches || this.frame !== null) return;
      // One frame per event batch; no clock, easing, or self-scheduling loop.
      this.frame = requestAnimationFrame(this.update);
    }

    update() {
      this.frame = null;
      if (!this.active || !this.isConnected || this.motion.matches || !this.anchor.getClientRects().length) return;

      const viewportHeight = window.innerHeight;
      if (viewportHeight <= 0) return;

      const bounds = this.anchor.getBoundingClientRect();
      const center = bounds.top + bounds.height / 2;
      // -15deg at the bottom of the viewport, +15deg at the top.
      // The same scroll position always produces the same angle in either direction.
      const progress = Math.min(1, Math.max(0, (viewportHeight - center) / viewportHeight));
      this.style.setProperty('--scroll-sticker-angle', `${(-15 + 30 * progress).toFixed(3)}deg`);
    }
  }

  customElements.define(tagName, ScrollSticker);
})();
